import {wrapAll} from '@malt/dom';
import {debounce} from '@malt/common';

Hop.defineModule('m-carousel', (root, param) => {
    const defaultOptions = {
        carouselClass: 'm-carousel',
        desactivate: false,
        heightAuto: false,
        paddingRight: 0,
        dots: true,
        nbItems: 1,
    };
    const responsive = param.responsive || {};
    // options can be modified on window resize, see setParametersByDeviceWidth()
    let options = Object.assign(defaultOptions, param);

    // Classes names
    const itemClass = `${options.carouselClass}__item`;
    const wrapperItemsClass = `${options.carouselClass}__wrapper`;
    const stageClass = `${options.carouselClass}__stage`;

    const items = Array.from(root.children);
    let maxIndexTranslatable;
    let xDown = null;
    let yDown = null;

    let wrapperElement;
    let stageEl;
    let indicators = root.querySelector('joy-indicators');
    let dots = root.querySelectorAll('joy-indicator');
    let currentItem = 0;
    let currentDot = 0;

    const debounceHandleResize = debounce(handleResize, 100);

    function init() {
        setParametersByDeviceWidth();
        initWindowEventListener();

        // Don't init Carousel if only 1 item Or desactivate : true
        if (items.length <= 1 || options.desactivate) {
            return;
        }

        // Recalculate Widths elements if Carousel already exist
        if (isCarouselExist()) {
            setCalculatedWidth();
            return;
        }

        // Init
        initClass();
        generateWrapperItemOutput();
        setCalculatedWidth();

        if (dotsHaveToBeGenerated()) {
            generateIndicatorsOutput(items);
        }

        if (options.heightAuto) {
            giveActiveItemHeightToList();
        }

        initCarouselEventListener();
    }

    function initWindowEventListener() {
        window.addEventListener('resize', debounceHandleResize, {passive: true});
    }

    function initCarouselEventListener() {
        stageEl.addEventListener('touchstart', handleTouchStart, {passive: true});
        stageEl.addEventListener('touchmove', handleTouchMove, {passive: true});
        if (options.dots) {
            indicators.addEventListener('click', handleClickOnDots);
        }
    }

    function deleteWindowEventListener() {
        window.removeEventListener('resize', debounceHandleResize);
    }

    function deleteCarouselEventListener() {
        if (stageEl) {
            stageEl.removeEventListener('touchstart', handleTouchStart);
            stageEl.removeEventListener('touchmove', handleTouchMove);
        }

        dots.forEach((dot) => {
            dot.removeEventListener('click', handleClickOnDots);
        });
    }

    function dotsHaveToBeGenerated() {
        dots = root.parentNode.querySelectorAll('joy-indicator');
        return options.dots && (!dots || dots.length === 0);
    }

    function setParametersByDeviceWidth() {
        let newOptions = {};
        let isOptionsChanged = false;
        let match = -1;

        Object.keys(responsive).forEach((breakpoint) => {
            if (breakpoint <= window.innerWidth && breakpoint >= match) {
                match = Number(breakpoint);
            }
        });
        newOptions = Object.assign(newOptions, defaultOptions, param, responsive[match]);
        newOptions.responsive = null;
        maxIndexTranslatable = items.length - newOptions.nbItems;

        if (JSON.stringify(newOptions) !== JSON.stringify(options)) {
            isOptionsChanged = true;
            options = newOptions;
        }

        return isOptionsChanged;
    }

    function handleClickOnDots(e) {
        const idxTarget = Array.from(e.target.parentNode.children).indexOf(e.target);
        if (idxTarget !== currentItem && idxTarget < dots.length) {
            translateList(idxTarget * options.nbItems);
        }
    }

    function refreshCalculatedSizes() {
        setCalculatedWidth();

        if (options.height) {
            giveActiveItemHeightToList();
        }
    }

    function generateIndicatorsOutput(items) {
        const list = document.createElement('joy-indicators');

        items.forEach((item, i) => {
            if (i % options.nbItems === 0) {
                const listItem = document.createElement('joy-indicator');

                if (i === 0) {
                    listItem.selected = true;
                }

                list.appendChild(listItem);
            }
        });

        root.appendChild(list);
        indicators = root.querySelector('joy-indicators');
        dots = root.querySelectorAll('joy-indicator');
    }

    function generateWrapperItemOutput() {
        const children = root.childNodes;
        wrapperElement = document.createElement('div');
        stageEl = document.createElement('div');
        wrapperElement.classList.add(wrapperItemsClass);
        stageEl.classList.add(stageClass);
        wrapAll(children, stageEl);
        wrapAll(children, wrapperElement);
    }

    function destroyWrapperOutput() {
        const docFragRoot = document.createDocumentFragment();
        items.forEach((child) => {
            docFragRoot.appendChild(child);
        });
        root.replaceChild(docFragRoot, wrapperElement);
    }

    function initClass() {
        root.classList.add(options.carouselClass);

        if (options.height) {
            root.classList.add(`${options.carouselClass}_height-item`);
        }

        items.forEach((item) => {
            item.classList.add(itemClass);
        });
    }

    function changeCurrentItem(n) {
        currentItem = (n + items.length) % items.length;
        currentDot = Math.floor(currentItem / options.nbItems);
    }

    function activateCurrentDot() {
        if (dots && dots[currentDot]) {
            dots[currentDot].selected = true;
        }
    }

    function desactivateCurrentDot() {
        if (dots && dots[currentDot]) {
            dots[currentDot].selected = false;
        }
    }

    function isCarouselExist() {
        return root.classList.contains(options.carouselClass);
    }

    function setCalculatedWidth() {
        const wrapperWidth = parseInt(window.getComputedStyle(root).getPropertyValue('width'));
        const itemWidth = (wrapperWidth - options.paddingRight) / options.nbItems;
        let listWidth = 0;
        for (const item of items) {
            item.style.width = itemWidth + 'px';
            listWidth += itemWidth;
        }
        stageEl.style.width = listWidth + 'px';
    }

    function translateList(idx) {
        const listWidth = parseInt(window.getComputedStyle(stageEl).getPropertyValue('width'));
        const wrapperWidth = parseInt(window.getComputedStyle(root).getPropertyValue('width'));
        let xPosition = (listWidth / items.length) * idx;

        if (xPosition > listWidth - wrapperWidth) {
            xPosition = listWidth - wrapperWidth;
        }

        if (idx < 0) {
            idx = 0;
            return;
        } else if (idx > maxIndexTranslatable) {
            idx = maxIndexTranslatable;
            return;
        }

        stageEl.style.transform = 'translate3d(-' + xPosition + 'px, 0px, 0px)';
        desactivateCurrentDot();
        changeCurrentItem(idx);
        activateCurrentDot();
        refreshCalculatedSizes();
    }

    function giveActiveItemHeightToList() {
        stageEl.style.height = items[currentItem].offsetHeight + 'px';
    }

    function handleResize() {
        const newOptions = setParametersByDeviceWidth();

        if (newOptions) {
            if (options.desactivate && isCarouselExist()) {
                generateWrapperItemOutput();
                return destroyCarousel();
            } else {
                init();
            }
        } else {
            if (isCarouselExist()) {
                setCalculatedWidth();
                translateList(currentItem);
            }
        }
    }

    function getTouches(event) {
        return event.touches || event.originalEvent.touches;
    }

    function handleTouchStart(event) {
        const firstTouch = getTouches(event)[0];
        xDown = firstTouch.clientX;
        yDown = firstTouch.clientY;
    }

    function handleTouchMove(event) {
        if (!xDown || !yDown) {
            return;
        }

        const xUp = event.touches[0].clientX;
        const yUp = event.touches[0].clientY;

        const xDiff = xDown - xUp;
        const yDiff = yDown - yUp;

        if (Math.abs(xDiff) > Math.abs(yDiff)) {
            if (xDiff > 0) {
                /* left swipe */
                translateList(currentItem + 1);
            } else {
                /* right swipe */
                translateList(currentItem - 1);
            }
        }

        /* reset values */
        xDown = null;
        yDown = null;
    }

    init();

    function destroyCarousel() {
        deleteCarouselEventListener();
        root.classList.remove(options.carouselClass, `${options.carouselClass}_${options.type}`);
        stageEl.style.width = '';
        stageEl.style.transform = '';
        if (options.height) {
            stageEl.style.height = '';
        }
        items.forEach((item) => {
            item.classList.remove('active', itemClass, 'to-left', 'to-right', 'from-left', 'from-right');
            item.style.width = '';
        });

        if (indicators) {
            indicators.remove();
        }

        destroyWrapperOutput();
        changeCurrentItem(0);
    }

    return function destroy() {
        deleteWindowEventListener();
        deleteCarouselEventListener();
    };
});
